/**
 * Professional Options Dashboard API service (Wave 3).
 *
 * Talks only to this app's own FastAPI backend (`/api/v1/options-dashboard/*`)
 * — same read-only convention as `oiScoreService`/`marketOpportunityService`:
 * every number shown by this page was already computed server-side (the
 * scoring engine, or a documented read-time derivation in
 * `options_dashboard_query_service`). This service never calculates
 * anything, it only fetches what the backend already produced.
 */

import api from "@/services/api";
import type {
  ChainRowRead,
  ExpiryListResponse,
  HistoryPoint,
  MarketEventRead,
  OptionsDashboardSummary,
  PaginatedResponse,
  StrategyInsightsResponse,
} from "@/types";

export interface SummaryParams {
  trade_date?: string;
  expiry_date?: string;
}

export interface HistoryParams {
  from_date: string;
  to_date: string;
  expiry_date?: string;
  page?: number;
  page_size?: number;
}

export interface EventsParams {
  trade_date?: string;
  page?: number;
  page_size?: number;
}

export interface ChainParams {
  expiry_date?: string;
  page?: number;
  page_size?: number;
  sort_by?: "strike" | "call_oi" | "call_oi_change" | "put_oi" | "put_oi_change" | "pcr";
  sort_order?: "asc" | "desc";
}

export const optionsDashboardService = {
  /** Sections 1-4 combined "current state" payload for one instrument. */
  async getSummary(symbol: string, params: SummaryParams = {}): Promise<OptionsDashboardSummary> {
    const { data } = await api.get<OptionsDashboardSummary>(`/options-dashboard/${symbol}/summary`, {
      params,
    });
    return data;
  },

  /** Section 5's 4 synced time-series charts — one joined series. */
  async getHistory(symbol: string, params: HistoryParams): Promise<PaginatedResponse<HistoryPoint>> {
    const { data } = await api.get<PaginatedResponse<HistoryPoint>>(`/options-dashboard/${symbol}/history`, {
      params,
    });
    return data;
  },

  /** Section 6's paginated, optionally date-filtered historical event log. */
  async getEvents(symbol: string, params: EventsParams = {}): Promise<PaginatedResponse<MarketEventRead>> {
    const { data } = await api.get<PaginatedResponse<MarketEventRead>>(`/options-dashboard/${symbol}/events`, {
      params,
    });
    return data;
  },

  /** Section 9's latest-N market alerts — same table as getEvents, just the recent-N view. */
  async getAlerts(symbol: string, limit = 10): Promise<MarketEventRead[]> {
    const { data } = await api.get<MarketEventRead[]>(`/options-dashboard/${symbol}/alerts`, {
      params: { limit },
    });
    return data;
  },

  /** Section 7's strike-wise merged CE/PE OI change table. */
  async getChain(symbol: string, params: ChainParams = {}): Promise<PaginatedResponse<ChainRowRead>> {
    const { data } = await api.get<PaginatedResponse<ChainRowRead>>(`/options-dashboard/${symbol}/chain`, {
      params,
    });
    return data;
  },

  /** Distinct expiry dates available for this instrument, for the filter dropdown. */
  async getExpiries(symbol: string): Promise<ExpiryListResponse> {
    const { data } = await api.get<ExpiryListResponse>(`/options-dashboard/${symbol}/expiries`);
    return data;
  },

  /** Section 8's static educational content, keyed by classic OI regime — no path param. */
  async getStrategyInsights(): Promise<StrategyInsightsResponse> {
    const { data } = await api.get<StrategyInsightsResponse>("/options-dashboard/strategy-insights");
    return data;
  },
};

export default optionsDashboardService;
